import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,  // Important for cookies/sessions
});

// Surface backend usage-cap (HTTP 429) responses as a global event so the app
// can show a single friendly modal instead of a raw error.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 429) {
      const message =
        error.response?.data?.detail ||
        "We've temporarily paused this tool while we look into unusual account usage.";
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("usage-limit", { detail: message }));
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const forgotPassword = (email) => api.post("/auth/forgot-password", { email });
export const resetPassword = (token, new_password) => api.post("/auth/reset-password", { token, new_password });
export const changePassword = (current_password, new_password) => api.post("/auth/change-password", { current_password, new_password });
export const updateProfile = (data) => api.put("/auth/profile", data);

// Admin
export const deleteUser = (userId) => api.delete(`/admin/users/${userId}`);

// Properties
export const getProperties = () => api.get("/properties");
export const getProperty = (id) => api.get(`/properties/${id}`);
export const createProperty = (data) => api.post("/properties", data);
export const updateProperty = (id, data) => api.put(`/properties/${id}`, data);
export const deleteProperty = (id) => api.delete(`/properties/${id}`);

// Expenses
export const getExpenses = (propertyId) => api.get(`/expenses/${propertyId}`);
export const createExpense = (data) => api.post("/expenses", data);
export const deleteExpense = (id) => api.delete(`/expenses/${id}`);

// Cashflow
export const getCashflow = (propertyId, taxableIncome = null) => {
  const params = taxableIncome ? `?taxable_income=${taxableIncome}` : '';
  return api.get(`/cashflow/${propertyId}${params}`);
};

export const getCombinedCashflow = (taxableIncome = null) =>
  api.get(`/cashflow-combined${taxableIncome ? `?taxable_income=${taxableIncome}` : ''}`);

// Portfolio
export const getPortfolioSummary = () => api.get("/portfolio/summary");

// Forecasting
export const forecastProperty = (propertyId, settings) => 
  api.post(`/forecast/${propertyId}`, settings);
export const forecastPortfolio = (settings) => 
  api.post("/forecast/portfolio", settings);

// Tax
export const calculateStampDuty = (price, state) => 
  api.get(`/tax/stamp-duty?price=${price}&state=${state}`);
export const calculateLandTax = (propertyId) => 
  api.get(`/tax/land-tax/${propertyId}`);
export const calculateCGT = (data) => api.post("/tax/cgt", data);

// Recommendations
export const getRecommendations = () => api.get("/recommendations");

// 2026-27 Budget tax reform: classify a property's negative-gearing/CGT treatment.
// Budget night 12 May 2026; reforms effective 1 Jul 2027.
export const BUDGET_NIGHT = new Date("2026-05-12T00:00:00Z");
export const getTaxCategory = (property) => {
  if (!property) return null;
  const pd = property.purchase_date ? new Date(property.purchase_date) : null;
  const heldBeforeBudget = pd && pd < BUDGET_NIGHT;
  if (heldBeforeBudget) {
    return { key: "grandfathered", label: "Grandfathered", className: "bg-slate-100 text-slate-600 border-slate-200" };
  }
  if (property.is_new_build) {
    return { key: "new_build", label: "New build", className: "bg-emerald-100 text-emerald-700 border-emerald-200" };
  }
  return { key: "established_new", label: "Established (post-reform)", className: "bg-amber-100 text-amber-700 border-amber-200" };
};

// Format currency
export const formatCurrency = (value) => {
  if (value === null || value === undefined) return "$0";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Format percentage
export const formatPercent = (value) => {
  if (value === null || value === undefined) return "0%";
  return `${value.toFixed(1)}%`;
};

// Purchase types for stamp duty
export const PURCHASE_TYPES = [
  { value: "ppor", label: "Principal Place of Residence (PPOR)" },
  { value: "investment", label: "Investment Property" },
  { value: "company", label: "Company Structure" },
  { value: "trust", label: "Trust Structure" },
  { value: "smsf", label: "SMSF" },
];

// Calculate stamp duty with purchase type
export const calculateStampDutyAdvanced = (data) => 
  api.post("/tax/stamp-duty", data);

// Australian states
export const STATES = [
  { value: "NSW", label: "New South Wales" },
  { value: "VIC", label: "Victoria" },
  { value: "QLD", label: "Queensland" },
  { value: "SA", label: "South Australia" },
  { value: "WA", label: "Western Australia" },
  { value: "TAS", label: "Tasmania" },
  { value: "NT", label: "Northern Territory" },
  { value: "ACT", label: "Australian Capital Territory" },
];

// Property types
export const PROPERTY_TYPES = [
  { value: "house", label: "House" },
  { value: "unit", label: "Unit/Apartment" },
  { value: "townhouse", label: "Townhouse" },
  { value: "land", label: "Land" },
  { value: "commercial", label: "Commercial" },
];

// Expense categories
export const EXPENSE_CATEGORIES = [
  { value: "council_rates", label: "Council Rates" },
  { value: "water", label: "Water Rates" },
  { value: "insurance", label: "Insurance" },
  { value: "management_fees", label: "Property Management" },
  { value: "repairs", label: "Repairs & Maintenance" },
  { value: "strata", label: "Strata/Body Corp" },
  { value: "other", label: "Other" },
];

// Frequency options
export const FREQUENCIES = [
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "annually", label: "Annually" },
];
